import React, { useEffect, useState } from 'react';
import TrackingHeader from 'components/ui/tracking-header';
import classNames from 'classnames';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import UserWebAPI from 'utils/UserWebAPIUtils';
import moment from 'moment-timezone';
import { useTranslation } from 'react-i18next';
import { titleize } from 'underscore.string';
import PencilIcon from '../../v2/icons/PencilIcon';
import AdminWebAPIUtils from '../../../utils/AdminWebAPIUtils';
import SessionStore from '../../../stores/SessionStore';
import { showToast } from '../../../stores/ToastStore';

export default function EmptyReturnInstructions() {
  const [fileData, setFileData] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [lastModifiedAt, setLastModifedAt] = useState(new Date());
  const isLoggedIn = SessionStore.isLoggedIn();
  const isAdmin = SessionStore.isAdmin();
  const { t } = useTranslation();
  const containerClassNames = classNames({
    absolute: !isLoggedIn,
    relative: isLoggedIn,
    'top-0': 1,
    'left-0': 1,
    'w-full': 1,
  });

  useEffect(() => {
    if (!editMode) {
      fetchInstructions();
    }
  }, [editMode]);

  useEffect(() => {
    SessionStore.addChangeListener(handleSessionChange);
    return () => {
      SessionStore.removeChangeListener(handleSessionChange);
    };
  }, []);

  const handleSessionChange = () => {
    setEditMode(false);
  };

  const fetchInstructions = async () => {
    const response = await UserWebAPI.getEmptyReturnInstructions();
    setFileData(response.data);
    setLastModifedAt(response.lastModifiedAt);
  };

  const uploadFile = async () => {
    const file = new Blob([fileData], { type: 'text/html' });
    const uploadResult = await AdminWebAPIUtils.uploadEmptyReturnInstructions([
      file,
    ]);
    if (uploadResult.success) {
      showToast(
        t('INSTRUCTIONS_UPLOAD_SUCCESSFULLY '),
        t('INSTRUCTIONS_UPDATED_SUCCESSFULLY'),
        'success',
      );
      setEditMode(false);
    } else {
      showToast(
        t('INSTRUCTIONS_UPLOAD_FAILED'),
        t('INSTRUCTIONS_UPDATION_FAILED'),
        'error',
      );
    }
  };

  const toggleEdit = () => {
    setEditMode(!editMode);
  };

  return (
    <>
      <div className={containerClassNames}>
        <TrackingHeader isLoggedIn={isLoggedIn} />
      </div>
      <div className="px-8" style={{ height: '100vh' }}>
        <div className="flex items-baseline pt-4 pl-4">
          <p className="text-2xl">{t('EMPTY_RETURN_INSTRUCTIONS')} </p>{' '}
          <button
            className="button"
            onClick={toggleEdit}
            hidden={!(isLoggedIn && isAdmin)}
          >
            <PencilIcon />
          </button>
          <span className="my-0 text-sm font-small text-gray-500 ml-2">
            ({t('LAST_UPDATED_ON')}:{' '}
            {moment(lastModifiedAt).tz('America/Los_Angeles').format('L LT')})
          </span>
        </div>
        {editMode ? (
          <div className="mt-2 px-4 h-4/5">
            <ReactQuill
              className=" bg-white w-4/5 mt-2"
              theme="snow"
              value={fileData}
              modules={{
                toolbar: [
                  [{ header: '1' }, { header: '2' }, { font: [] }],
                  [{ size: [] }],
                  ['bold', 'italic', 'underline', 'strike', 'blockquote'],
                  [{ align: [] }],
                  [{ color: [] }, { background: [] }],
                  ['link'],
                  [
                    { list: 'ordered' },
                    { list: 'bullet' },
                    { indent: '-1' },
                    { indent: '+1' },
                  ],
                  ['link', 'code-block'],
                  ['clean'],
                ],
              }}
              formats={[
                'header',
                'font',
                'size',
                'bold',
                'italic',
                'underline',
                'strike',
                'blockquote',
                'color',
                'background',
                'list',
                'bullet',
                'indent',
                'link',
                'code-block',
                'align',
                'script',
              ]}
              onChange={(value) => setFileData(value)}
            />
            <div className="my-2 w-4/5 flex justify-end space-x-4">
              <button onClick={() => setEditMode(false)} className="button">
                {t('CANCEL')}
              </button>

              <button
                onClick={() => uploadFile()}
                className="min-w-12 button button-green"
              >
                {titleize(t('SAVE'))}
              </button>
            </div>
          </div>
        ) : (
          <ViewPage content={fileData} />
        )}
      </div>
    </>
  );
}

const ViewPage = ({ content }) => {
  const createMarkup = () => {
    return { __html: content };
  };
  return (
    <div className="w-3/5 p-4 min-h-96 max-h-full mt-4">
      <div dangerouslySetInnerHTML={createMarkup()} />
    </div>
  );
};
